/**
 * 爱微圈导航 · 搜索 / 搜索引擎
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export const TAB_TARGETS = {
    web: { google: 'https://www.google.com/search?q={q}', baidu: 'https://www.baidu.com/s?wd={q}', bing: 'https://www.bing.com/search?q={q}' },
    images: { google: 'https://www.google.com/search?q={q}&tbm=isch', baidu: 'https://image.baidu.com/search/index?tn=baiduimage&word={q}', bing: 'https://www.bing.com/images/search?q={q}' },
    news: { google: 'https://news.google.com/search?q={q}', baidu: 'https://www.baidu.com/s?tn=news&word={q}', bing: 'https://www.bing.com/news/search?q={q}' },
    videos: { google: 'https://www.youtube.com/results?search_query={q}', baidu: 'https://video.baidu.com/?word={q}', bing: 'https://www.bing.com/videos/search?q={q}' },
    map: { google: 'https://www.google.com/maps/search/{q}', baidu: 'https://map.baidu.com/search/{q}', bing: 'https://www.bing.com/maps?q={q}' },
};


export function engineFamily() {
    const eng = currentEngine();
    if (!eng) return 'baidu';
    const tpl = eng.url_template || '';
    if (tpl.includes('google')) return 'google';
    if (tpl.includes('bing.com')) return 'bing';
    if (tpl.includes('baidu')) return 'baidu';
    return null;
}

export function currentEngine() {
    const id = state.settings.search_engine;
    const all = [...state.engines.builtin.map(e => ({ ...e, _id: 'b' + e.id })), ...state.engines.custom.map(e => ({ ...e, _id: e.id }))];
    return all.find(e => e._id === id) || all[0] || null;
}

export function handleSearch() {
    const input = $('#searchInput');
    const q = input.value.trim();
    if (!q) return;
    // 网址直达
    if (state.searchTab === 'web' && /^https?:\/\//i.test(q)) { openTarget(q); return; }
    const eng = currentEngine();
    const fam = engineFamily();
    let url = null;
    if (fam && TAB_TARGETS[state.searchTab] && TAB_TARGETS[state.searchTab][fam]) {
        url = TAB_TARGETS[state.searchTab][fam].replace('{q}', encodeURIComponent(q));
    } else if (eng && eng.url_template) {
        url = eng.url_template.replace('{query}', encodeURIComponent(q));
    } else {
        url = 'https://www.baidu.com/s?wd=' + encodeURIComponent(q);
    }
    openTarget(url);
    if (state.settings.keep_search_content !== '1' && state.searchTab === 'web') input.value = '';
    hideSuggestions();
}

export function openTarget(url) {
    if (state.settings.open_search_new_tab === '1') window.open(url, '_blank', 'noopener');
    else location.href = url;
}

/* 搜索建议 */

let suggestReqId = 0;

export async function fetchSuggestions(q) {
    const reqId = ++suggestReqId;
    try {
        const list = await apiGet(API.suggest + '?q=' + encodeURIComponent(q));
        if (reqId !== suggestReqId) return; // 过期请求，丢弃
        const box = $('#searchSuggestions');
        if (!q || !list.length) { box.classList.remove('show'); box.innerHTML = ''; return; }
        box.innerHTML = list.map((r, i) => {
            const ico = r.type === 'shortcut' ? '⚡' : r.type === 'website' ? '🌐' : '🔍';
            return `<div class="suggest-item" data-i="${i}"><span class="s-ico">${ico}</span><span class="s-text">${esc(r.text)}</span><span class="s-url">${esc(r.url)}</span></div>`;
        }).join('');
        box.classList.add('show');
        $$('.suggest-item', box).forEach(el => {
            el.onmousedown = (e) => {
                e.preventDefault();
                const r = list[+el.dataset.i];
                if (r.url) { if (state.settings.open_search_new_tab === '1') window.open(safeUrl(r.url) || r.url, '_blank', 'noopener'); else location.href = safeUrl(r.url) || r.url; }
            };
        });
    } catch (e) { /* ignore */ }
}

export function hideSuggestions() { const b = $('#searchSuggestions'); b.classList.remove('show'); b.innerHTML = ''; }

/* 引擎下拉 */

export function engineBadgeHtml(e) {
    // 接受绝对 URL(http/https)与根相对路径(/api/icon_proxy、/uploads/...)；
    // 旧版本只认 http(s) 绝对 URL，导致接入 icon_proxy 相对路径后直接走 letter 分支，
    // 所有引擎都看不到真 logo。data-letter 用于 onerror 时回退首字母字符。
    if (e && e.icon && (/^https?:/i.test(e.icon) || /^\//.test(e.icon))) {
        return `<img src="${escAttr(e.icon)}" alt="" class="engine-icon-img" data-letter="${escAttr((e.name || '?').charAt(0).toUpperCase())}">`;
    }
    const letter = (e && e.name ? e.name : '?').charAt(0).toUpperCase();
    return `<span class="engine-letter-sm">${esc(letter)}</span>`;
}

/* 给当前容器内所有 .engine-icon-img 绑定 onerror 回退（属性绑定避开内联事件触发 CSP）。
 * img 是 renderEngineDropdown / renderEngineBadge 重新插入的，每次渲染后调用一次即可。 */

export function bindEngineIconFallback(root) {
    const scope = root || document;
    $$('.engine-icon-img', scope).forEach(img => {
        if (img.__fbBound) return;
        img.__fbBound = true;
        img.onerror = function () {
            const letter = this.getAttribute('data-letter') || '?';
            this.replaceWith(Object.assign(document.createElement('span'), {
                className: 'engine-letter-sm',
                textContent: letter,
            }));
        };
    });
}


export function renderEngineDropdown() {
    const dd = $('#engineDropdown');
    const builtin = state.engines.builtin.map(e => ({ ...e, _id: 'b' + e.id }));
    const custom = state.engines.custom.map(e => ({ ...e, _id: e.id }));
    const all = [...builtin, ...custom];
    if (!state.settings.search_engine || !all.find(e => e._id === state.settings.search_engine)) {
        state.settings.search_engine = all.length ? all[0]._id : '';
    }
    const cur = currentEngine();
    dd.innerHTML = `<div class="engine-grid">${all.map(e => `
        <div class="engine-option ${e._id === state.settings.search_engine ? 'active' : ''}" data-id="${escAttr(e._id)}">
            <span class="engine-badge-sm">${engineBadgeHtml(e)}</span>
            <span class="engine-name">${esc(e.name)}</span>
        </div>`).join('')}</div>
        <div class="engine-option engine-add" id="engineAddBtn">＋ 添加自定义引擎</div>`;

    $$('.engine-option', dd).forEach(el => {
        el.onclick = async (ev) => {
            if (ev.target.classList.contains('engine-del')) return;
            state.settings.search_engine = el.dataset.id;
            scheduleSave();
            renderEngineBadge(currentEngine());
            renderEngineDropdown();
            dd.classList.remove('show');
        };
    });
    $$('.engine-del', dd).forEach(el => {
        el.onclick = async (ev) => {
            ev.stopPropagation();
            try {
                const res = await apiPost(API.engines + '?action=remove', { id: el.dataset.del });
                state.engines.custom = res.custom;
                if (state.settings.search_engine === el.dataset.del) state.settings.search_engine = '';
                renderEngineDropdown();
                toast('已删除自定义引擎', 'success');
            } catch (e) { toast(e.message, 'error'); }
        };
    });
    $('#engineAddBtn').onclick = () => showEngineAddModal();
    if (cur) renderEngineBadge(cur);
    bindEngineIconFallback(dd);
}

export function renderEngineBadge(e) {
    const iconEl = $('#engineIcon');
    const letterEl = $('#engineLetter');
    if (!letterEl) return;
    const letter = (e && e.name ? e.name : '?').charAt(0).toUpperCase();
    // 与 engineBadgeHtml 一致：同时接受绝对 URL 与根相对路径（icon_proxy）
    if (e && e.icon && (/^https?:/i.test(e.icon) || /^\//.test(e.icon)) && iconEl) {
        iconEl.src = e.icon;
        iconEl.style.display = '';
        iconEl.onerror = function () { this.style.display = 'none'; letterEl.style.display = ''; };
        letterEl.style.display = 'none';
        letterEl.textContent = letter;
    } else {
        if (iconEl) { iconEl.style.display = 'none'; iconEl.src = ''; }
        letterEl.style.display = '';
        letterEl.textContent = letter;
    }
}

export function showEngineAddModal() {
    $('#engineDropdown').classList.remove('show');
    openModal(`
        <div class="modal-title">添加自定义搜索引擎</div>
        <div class="modal-error" id="engineErr"></div>
        <div class="form-group"><label>名称</label><input type="text" class="form-input" id="engName" placeholder="如：搜狗" maxlength="20"></div>
        <div class="form-group"><label>地址模板（包含 {query} 占位符）</label><input type="text" class="form-input" id="engUrl" placeholder="https://www.sogou.com/web?query={query}"></div>
        <div class="modal-actions">
            <button class="btn btn-light" data-close-modal>取消</button>
            <button class="btn btn-primary" id="engSave">添加</button>
        </div>`);
    $('#engSave').onclick = async () => {
        const name = $('#engName').value.trim();
        const url = $('#engUrl').value.trim();
        try {
            const res = await apiPost(API.engines + '?action=add', { name, url_template: url });
            state.engines.custom = res.custom;
            closeModal();
            renderEngineDropdown();
            toast('已添加自定义引擎', 'success');
        } catch (e) { showModalError('#engineErr', e.message); }
    };
}

/* ---------- 快捷方式 ---------- */

